import { cliente } from '@/lib/cliente';
import type { AsignarRolRequest, ListaUsuariosResponse, RespuestaApi } from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Hooks de gestión de roles de usuarios (admin).
 *
 * Backend hace todo el round-trip a Microsoft Graph — el frontend solo
 * consume `RespuestaApi<ListaUsuariosResponse>` y dispara mutaciones.
 */

export function useUsuarios(filtros: { q?: string; limite?: number } = {}) {
  return useQuery<RespuestaApi<ListaUsuariosResponse>>({
    queryKey: ['usuarios', filtros],
    queryFn: () => cliente.usuarios.listar(filtros),
    // Roles cambian poco — 30s es suficiente para evitar trash y a la vez
    // refrescar pronto después de asignar/quitar.
    staleTime: 30_000,
  });
}

export function useAsignarRol() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<{ assignmentId: string; yaExistia: boolean }>,
    Error,
    { userId: string; rol: AsignarRolRequest['rol'] }
  >({
    mutationFn: ({ userId, rol }) => cliente.usuarios.asignarRol(userId, { rol }),
    onSuccess: (r, vars) => {
      if (r.data) {
        toast.success(
          r.data.yaExistia
            ? `El usuario ya tenía el rol "${vars.rol}"`
            : `Rol "${vars.rol}" asignado`,
        );
        qc.invalidateQueries({ queryKey: ['usuarios'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo asignar el rol', { description: e.message });
    },
  });
}

export function useQuitarRol() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<{ ok: true }>,
    Error,
    { userId: string; assignmentId: string; rolLabel?: string }
  >({
    mutationFn: ({ userId, assignmentId }) => cliente.usuarios.quitarRol(userId, assignmentId),
    onSuccess: (r, vars) => {
      if (r.data) {
        toast.success(vars.rolLabel ? `Rol "${vars.rolLabel}" quitado` : 'Rol quitado');
        qc.invalidateQueries({ queryKey: ['usuarios'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo quitar el rol', { description: e.message });
    },
  });
}
